import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "agent") navigate("/agent");
      else if (user.role === "staff") navigate("/staff");
      else navigate("/customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-body">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0A183D] via-[#1B3FA0] to-[#0EA5E9] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1717700299591-470e043edc9c?w=1400&q=80')", backgroundSize: "cover" }} />
        <div className="relative">
          <SendtooLogo size="2xl" />
        </div>
        <div className="relative">
          <h2 className="font-heading text-5xl font-bold text-white uppercase leading-tight mb-4">Welcome Back to Sendtoo</h2>
          <p className="text-blue-200 text-lg">Your reliable logistics partner. Track, manage, and ship with confidence.</p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { value: "50K+", label: "Shipments" },
              { value: "120+", label: "Cities" },
              { value: "98%", label: "On-Time" },
              { value: "5K+", label: "Customers" },
            ].map((s) => (
              <div key={s.label} className="bg-white/10 rounded-lg p-4">
                <div className="font-heading text-2xl font-bold text-white">{s.value}</div>
                <div className="text-blue-200 text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-blue-200">
          © {new Date().getFullYear()} Sendtoo Logistics
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <SendtooLogo size="lg" align="center" />
          </div>

          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase mb-1">Sign In</h1>
          <p className="text-slate-500 text-sm mb-8">Enter your credentials to access your portal.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@email.com"
                required
                data-testid="login-email"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Your password"
                  required
                  data-testid="login-password"
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2 uppercase tracking-wide"
            >
              <LogIn className="w-4 h-4" />
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Don't have an account?{" "}
            <Link to="/auth/register" className="text-brand-500 font-semibold hover:underline">Register here</Link>
          </p>


          <p className="text-center text-xs text-slate-400 mt-6">
            <Link to="/" className="hover:text-slate-600">← Back to home</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
